import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { useOfferStore } from '@/lib/store';
import {
  classifyMaterialsSearchResponse,
  ermittleEffektivenMaterialAufschlagFuerVorschau,
} from '@shared/catalog-batch-suggest';
import {
  canCommitOfferDocumentTask,
  commitOfferDocumentWriteIfCurrent,
} from '@shared/batch-apply-ui-controller';
import {
  rankCatalogCandidates,
  unitsIncompatibleMitPriceUnitFallback,
  type CatalogArticleCandidate,
  type CatalogMatchZone,
  type RankedCatalogCandidate,
} from '@shared/catalog-position-match';
import { effectiveUnitPrice, nachDivisorEinheitLabel } from '@shared/effective-unit-price';
import {
  BLITZ_ZEILEN_IDS,
  direktEpImportBlockiertDurchBestandskalkulation,
  einheitspreisAusKalkulation,
  haengePreisHerkunftAnBlitzMaterial,
  hatKalkulation,
  materialEkImportBlockiertDurchBestandskalkulation,
  mengenBezugFuerPreisImport,
  nichtUebernehmbarWegenNullOderNegativemPreis,
  rundetStillAufNull,
  setzeBlitzZeile,
} from '@shared/kalkulation';
import { bewertePreisFrische, type PreisFrischeStufe } from '@shared/preis-frische';
import {
  baueLivePreisHerkunft,
  bewerteLiveQuoteEinheit,
  type LivePriceQuoteDto,
  type LiveQuoteFrische,
} from '@shared/live-price';
import type { Position, PositionsKalkulation } from '@shared/schema';
import { AlertTriangle, Loader2, PackageSearch, Search, Upload, Zap } from 'lucide-react';

type ApplyMode = 'material-ek' | 'direkt-ep';

/**
 * Katalogartikel wie von GET /api/softec-db/materials/search geliefert.
 * Bewusst als lokaler Typ dupliziert (statt aus server/routes/softec-db.ts zu
 * importieren) — Server-Code soll nicht ins Client-Bundle gezogen werden,
 * gleiches Muster wie client/src/components/customer-autocomplete.tsx.
 */
interface CatalogSearchResult {
  id: string;
  articleNumber: string;
  ean: string | null;
  description: string;
  descriptionLong: string | null;
  unit: string | null;
  price: number | null;
  priceUnit: string | null;
  // Preisfrische + Live-Preis-Naht (2026-08, additiv — ältere Server-Antworten ohne
  // die Felder bleiben gültig, dann gibt es schlicht kein Badge/keinen Live-Platz).
  supplierId?: string;
  priceValidFrom?: string | null;
  priceValidUntil?: string | null;
  updatedAt?: string | null;
}

/** Antwort von GET /api/softec-db/live-price-status. */
interface LiveStatusInfo {
  verfuegbar: boolean;
  status: string;
  hinweis?: string;
}

type LiveQuoteMitFrische = LivePriceQuoteDto & { frische: LiveQuoteFrische };

const FRISCHE_BADGE_CLASS: Record<PreisFrischeStufe, string> = {
  frisch: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  aelter: 'border-amber-300 bg-amber-50 text-amber-700',
  abgelaufen: 'border-red-300 bg-red-50 text-red-700',
  unbekannt: 'border-gray-200 bg-gray-50 text-gray-500',
};

function formatZeitpunkt(iso: string | null): string {
  if (!iso) return 'unbekannt';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return 'unbekannt';
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

const ZONE_BADGE_CLASS: Record<CatalogMatchZone, string> = {
  high: 'bg-emerald-600 hover:bg-emerald-600',
  medium: 'bg-amber-500 hover:bg-amber-500',
  low: 'bg-gray-400 hover:bg-gray-400',
};

const ZONE_LABEL: Record<CatalogMatchZone, string> = {
  high: 'hohe Konfidenz',
  medium: 'mittlere Konfidenz',
  low: 'niedrige Konfidenz',
};

function formatEuro(value: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

interface CatalogSuggestDialogProps {
  /** Die LV-Position, für die ein Katalogartikel vorgeschlagen werden soll. */
  position: Position;
  /** Positionsmenge — für die EP-Vorschau bei mengenBezug 'gesamt' (siehe shared/kalkulation.ts). */
  menge: number;
}

// P2-3 (Fix, Codex-Fund): symmetrisch zum Batch-Pfad (catalog-batch-suggest-dialog.tsx::
// SEARCH_RESULT_LIMIT) — 20 war zu knapp: die Route sortiert alphabetisch und begrenzt DANN auf
// `limit`, das Ranking (rankCatalogCandidates) läuft komplett client-seitig auf genau den
// zurückgelieferten Kandidaten. Bei >20 Treffern konnte der beste/exakte Kandidat außerhalb des
// abgeschnittenen alphabetischen Präfix liegen. Fix: das MAXIMAL erlaubte Server-Limit (50)
// anfragen (wie der Batch-Pfad), zusätzlich das serverseitige `truncated`-Flag auswerten (siehe
// server/routes/softec-db.ts::applySearchResultLimit) und sichtbar melden, statt es (wie bisher)
// komplett zu ignorieren.
const SEARCH_RESULT_LIMIT = 50;

/**
 * Katalog-Vorschlag pro Position: sucht gegen /api/softec-db/materials/search,
 * rankt die Treffer über rankCatalogCandidates (shared/catalog-position-match.ts)
 * und übernimmt einen ausgewählten Kandidaten per Klick — spiegelt dieselben
 * zwei Modi wie client/src/components/gaeb/X84PriceImport.tsx:
 * - material-ek (Default): Blitzkalkulations-Material-Zeile via setzeBlitzZeile
 *   + updatePositionKalkulation, mit Material-Aufschlag; Quelle wird als
 *   "Großhandel: <Artikelbezeichnung>" getaggt.
 * - direkt-ep: updatePosition(id, { preis }), ohne Aufschlag.
 *
 * HARTE Regel (EP-Bug-Klasse, siehe shared/effective-unit-price.ts): Softecs
 * Katalogpreis gilt pro `priceUnit`-vielen Einheiten (z.B. "100 M"). Es fließt
 * IMMER der über effectiveUnitPrice() aufgelöste effektive EK in die
 * Kalkulation/den EP — NIE der rohe `price`.
 */
export function CatalogSuggestDialog({ position, menge }: CatalogSuggestDialogProps) {
  const kalkulationsDefaults = useOfferStore((s) => s.kalkulationsDefaults);
  const offerDocumentEpoch = useOfferStore((s) => s.offerDocumentEpoch);
  const updatePosition = useOfferStore((s) => s.updatePosition);
  const updatePositionKalkulation = useOfferStore((s) => s.updatePositionKalkulation);
  const { toast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [rawResults, setRawResults] = useState<CatalogSearchResult[]>([]);
  const [searchResultContext, setSearchResultContext] = useState<{
    offerDocumentEpoch: number;
    positionId: number;
  } | null>(null);
  // P2-3 (Fix, Codex-Fund): true, wenn der Server mehr Treffer hatte, als zurückgegeben wurden
  // (siehe server/routes/softec-db.ts::applySearchResultLimit) — bisher komplett ignoriert.
  const [truncated, setTruncated] = useState(false);
  const [mode, setMode] = useState<ApplyMode>('material-ek');
  // Live-Preis-Naht (2026-08): Integrationsstatus je Lieferant + jüngste Quotes je
  // Artikelnummer — rein LESEND (kein Abruf-Trigger, der lebt in der Hauptapp).
  const [liveStatusBySupplier, setLiveStatusBySupplier] = useState<Record<string, LiveStatusInfo>>({});
  const [liveQuoteByKey, setLiveQuoteByKey] = useState<Record<string, LiveQuoteMitFrische>>({});
  const searchedOnceRef = useRef(false);
  // P2-7: Generation-Zähler gegen veraltete Suchantworten — zwei überlappende Suchen (schnelles
  // Neu-Tippen/erneutes Klicken auf "Suchen") können in beliebiger Reihenfolge auflösen; ohne
  // Guard würde die ÄLTERE Antwort, wenn sie zuletzt ankommt, neuere Ergebnisse (bzw. einen
  // bereits wieder auf false gesetzten Loading-State) überschreiben. Siehe
  // shared/catalog-position-match.ts::istAktuelleGeneration.
  const searchGenerationRef = useRef(0);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const renderedPositionIdRef = useRef(position.id);
  const lastProcessedOfferDocumentEpochRef = useRef(offerDocumentEpoch);
  renderedPositionIdRef.current = position.id;
  // P2-2 (Fix, Codex-Fund): tippt der Nutzer eine exakte Artikelnummer/EAN ins Suchfeld, lieferte
  // rankCatalogCandidates() bisher NIE einen harten Identifier-Treffer, weil `artikelnummer`
  // hier immer `null` war (Position trägt kein eigenes Artikelnummer-Feld) — das Ranking lief
  // ausschließlich über Fuzzy-Text-Score gegen name/beschreibung, unabhängig davon, WAS der
  // Nutzer tatsächlich gesucht hat. Ein exakt getroffener Artikel landete dadurch ggf. unter der
  // Top-5-Grenze (topN-Slice), obwohl die API ihn zurückgeliefert hatte. Fix: den zuletzt
  // ABGESCHICKTEN (nicht nur getippten) Suchbegriff als Identifier ans Ranking übergeben — ein
  // normalisierter Treffer gegen articleNumber/ean bekommt dadurch confidence=1 und steht so
  // sicher vor dem Slice (siehe shared/catalog-position-match.ts::rankCatalogCandidates).
  const lastSearchedTermRef = useRef<string>('');

  useEffect(() => {
    if (lastProcessedOfferDocumentEpochRef.current === offerDocumentEpoch) return;
    lastProcessedOfferDocumentEpochRef.current = offerDocumentEpoch;
    searchGenerationRef.current += 1;
    searchAbortControllerRef.current?.abort();
    searchAbortControllerRef.current = null;
    searchedOnceRef.current = false;
    lastSearchedTermRef.current = '';
    setIsOpen(false);
    setQuery('');
    setRawResults([]);
    setSearchResultContext(null);
    setSearchError(null);
    setTruncated(false);
    setIsSearching(false);
    setMode('material-ek');
    setLiveStatusBySupplier({});
    setLiveQuoteByKey({});
  }, [offerDocumentEpoch]);

  /**
   * Live-Preis-Naht: lädt NACH einer erfolgreichen Katalog-Suche den Integrationsstatus
   * je (distinktem) Lieferanten der Treffer und — nur bei status 'active' — die jüngsten
   * Quotes zu den Artikelnummern dieses Lieferanten. Fehler hier brechen NIE die
   * Katalog-Suche: der Live-Platz zeigt dann schlicht nichts an (Katalogpreis bleibt
   * ohnehin der Default). `canCommit` ist derselbe Generation-/Epoch-Guard wie für die
   * Suchantwort selbst — eine veraltete Live-Antwort schreibt nicht in den State.
   */
  const ladeLiveDaten = async (candidates: CatalogSearchResult[], canCommit: () => boolean) => {
    const supplierIds = Array.from(
      new Set(candidates.map((c) => c.supplierId).filter((s): s is string => Boolean(s))),
    ).slice(0, 5);
    if (supplierIds.length === 0) return;
    try {
      const statusEntries = await Promise.all(
        supplierIds.map(async (supplierId) => {
          const response = await fetch(
            `/api/softec-db/live-price-status?supplierId=${encodeURIComponent(supplierId)}`,
          );
          if (!response.ok) return [supplierId, null] as const;
          const data = (await response.json()) as LiveStatusInfo;
          return [supplierId, data] as const;
        }),
      );
      if (!canCommit()) return;
      const statusMap: Record<string, LiveStatusInfo> = {};
      for (const [supplierId, info] of statusEntries) {
        if (info) statusMap[supplierId] = info;
      }
      setLiveStatusBySupplier(statusMap);

      const aktiveSuppliers = statusEntries.filter(([, info]) => info?.verfuegbar && info.status === 'active');
      if (aktiveSuppliers.length === 0) return;
      const quoteEntries = await Promise.all(
        aktiveSuppliers.map(async ([supplierId]) => {
          const nummern = Array.from(
            new Set(
              candidates
                .filter((c) => c.supplierId === supplierId)
                .map((c) => c.articleNumber)
                .filter(Boolean),
            ),
          ).slice(0, 50);
          if (nummern.length === 0) return [supplierId, []] as const;
          const response = await fetch(
            `/api/softec-db/live-quotes?supplierId=${encodeURIComponent(supplierId)}&nummern=${encodeURIComponent(nummern.join(','))}`,
          );
          if (!response.ok) return [supplierId, []] as const;
          const data = (await response.json()) as { quotes?: LiveQuoteMitFrische[] };
          return [supplierId, Array.isArray(data.quotes) ? data.quotes : []] as const;
        }),
      );
      if (!canCommit()) return;
      const quoteMap: Record<string, LiveQuoteMitFrische> = {};
      for (const [supplierId, quotes] of quoteEntries) {
        for (const quote of quotes) {
          quoteMap[`${supplierId}::${quote.requestedNumber}`] = quote;
        }
      }
      setLiveQuoteByKey(quoteMap);
    } catch {
      // Live-Daten sind ein Zusatz — ein Fehler hier lässt den Dialog unverändert
      // funktionieren (kein Live-Platz statt eines kaputten Dialogs).
    }
  };

  const runSearch = async (term: string) => {
    const trimmed = term.trim();
    searchGenerationRef.current += 1;
    const startGeneration = searchGenerationRef.current;
    const startOfferDocumentEpoch = useOfferStore.getState().offerDocumentEpoch;
    const startPositionId = position.id;
    searchAbortControllerRef.current?.abort();
    const controller = new AbortController();
    searchAbortControllerRef.current = controller;
    const canCommit = () => canCommitOfferDocumentTask({
      startGeneration,
      currentGeneration: searchGenerationRef.current,
      expectedDocumentEpoch: startOfferDocumentEpoch,
      currentDocumentEpoch: useOfferStore.getState().offerDocumentEpoch,
      expectedPositionId: startPositionId,
      currentPositionId: renderedPositionIdRef.current,
    });

    if (trimmed.length === 0) {
      // P3-7 (Fix, Codex-Fund): diese Generation-Erhöhung invalidiert eine evtl. noch laufende
      // ÄLTERE Suche — deren eigenes `finally` erkennt das und überspringt deshalb
      // setIsSearching(false)/setSearchError(null). Ohne den expliziten Reset HIER bliebe
      // "Suche läuft..." hängen, wenn der Nutzer während einer laufenden Suche das Suchfeld
      // leert und Enter drückt.
      if (canCommit()) {
        setRawResults([]);
        setSearchResultContext(null);
        setTruncated(false);
        setIsSearching(false);
        setSearchError(null);
        setLiveStatusBySupplier({});
        setLiveQuoteByKey({});
      }
      return;
    }
    // P2-2: erst HIER (tatsächlich abgeschickte Suche), nicht bei jedem Tastendruck im
    // Eingabefeld — der Identifier soll den zuletzt SUBMITTETEN Suchbegriff widerspiegeln.
    lastSearchedTermRef.current = trimmed;
    setIsSearching(true);
    setSearchError(null);
    setSearchResultContext(null);
    try {
      const response = await fetch(
        `/api/softec-db/materials/search?q=${encodeURIComponent(trimmed)}&limit=${SEARCH_RESULT_LIMIT}`,
        { signal: controller.signal },
      );
      const data = await response.json();
      if (!canCommit()) return;
      if (!response.ok) {
        setRawResults([]);
        setSearchResultContext(null);
        setTruncated(false);
        setSearchError(data?.error || `HTTP ${response.status}`);
        return;
      }
      // P2-3: dieselbe, bereits unter node:test geprüfte Klassifizierung wie der Batch-Pfad
      // (siehe tests/catalog-batch-suggest.test.ts) — liefert Kandidaten UND das `truncated`-Flag
      // aus derselben Antwort, statt beides hier erneut (und potenziell abweichend) zu parsen.
      const classified = classifyMaterialsSearchResponse({ ok: true, data });
      if (!classified.ok) {
        setRawResults([]);
        setSearchResultContext(null);
        setTruncated(false);
        setSearchError('Suche fehlgeschlagen: ungültige Serverantwort.');
        return;
      }
      setRawResults(classified.candidates);
      setSearchResultContext({
        offerDocumentEpoch: startOfferDocumentEpoch,
        positionId: startPositionId,
      });
      setTruncated(classified.truncated);
      // Live-Preis-Naht: Status/Quotes zu den NEUEN Treffern nachladen (alte Zuordnung
      // sofort leeren, damit kein Quote eines vorherigen Suchlaufs an einem neuen
      // Treffer klebt). Bewusst fire-and-forget mit demselben canCommit-Guard.
      setLiveStatusBySupplier({});
      setLiveQuoteByKey({});
      void ladeLiveDaten(classified.candidates as CatalogSearchResult[], canCommit);
    } catch (error) {
      if (!canCommit()) return;
      setRawResults([]);
      setSearchResultContext(null);
      setTruncated(false);
      setSearchError(error instanceof Error ? error.message : 'Suche fehlgeschlagen');
    } finally {
      if (searchAbortControllerRef.current === controller) {
        searchAbortControllerRef.current = null;
      }
      if (canCommit()) setIsSearching(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && !searchedOnceRef.current) {
      searchedOnceRef.current = true;
      const initialQuery = position.name?.trim() || position.beschreibung?.trim() || '';
      setQuery(initialQuery);
      void runSearch(initialQuery);
    }
    if (!open) {
      // P2-7: laufende Suche invalidieren, damit eine spät ankommende Antwort nach dem
      // Schließen nicht mehr in den State schreibt.
      searchGenerationRef.current += 1;
      searchAbortControllerRef.current?.abort();
      searchAbortControllerRef.current = null;
      // Reset für den nächsten Öffnen-Vorgang, damit neue Positionsdaten greifen.
      searchedOnceRef.current = false;
      setMode('material-ek');
      setSearchError(null);
      setTruncated(false);
      setRawResults([]);
      setSearchResultContext(null);
      setIsSearching(false);
      setLiveStatusBySupplier({});
      setLiveQuoteByKey({});
      // P2-2: sonst würde beim nächsten Öffnen (andere Position) kurzzeitig noch der ALTE
      // Identifier fürs Ranking verwendet, bis der Auto-Fetch der neuen Position durchläuft.
      lastSearchedTermRef.current = '';
    }
  };

  const candidatesForRanking: CatalogArticleCandidate[] = rawResults;

  const ranked = useMemo<RankedCatalogCandidate[]>(
    () =>
      rankCatalogCandidates(
        {
          name: position.name ?? '',
          beschreibung: position.beschreibung,
          einheit: position.einheit,
          // P2-2 (Fix, Codex-Fund): Position trägt zwar kein eigenes Artikelnummer-Feld im
          // Datenmodell, ABER der Nutzer kann eine exakte Artikelnummer/EAN direkt ins
          // Suchfeld tippen (bzw. scannen). Vorher wurde das komplett ignoriert (immer
          // `null`) — ein exakter Treffer lief dann nur über Fuzzy-Text und konnte unter die
          // Top-5-Grenze rutschen, OBWOHL die API ihn zurückgeliefert hatte. Der zuletzt
          // abgeschickte Suchbegriff wird hier als Identifier durchgereicht — ein
          // normalisierter Treffer gegen articleNumber/ean bekommt dadurch confidence=1 und
          // steht sicher vor dem Slice.
          artikelnummer: lastSearchedTermRef.current || null,
        },
        candidatesForRanking,
        5,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lastSearchedTermRef.current wird
    // bewusst NICHT als Dependency geführt (Refs sind nicht reaktiv); rawResults ändert sich
    // durch setRawResults IMMER erst NACHDEM lastSearchedTermRef synchron gesetzt wurde (siehe
    // runSearch), wodurch dieser Re-Render bereits den aktuellen Ref-Wert liest.
    [position.name, position.beschreibung, position.einheit, candidatesForRanking],
  );

  const effectiveFor = (candidate: CatalogArticleCandidate) => {
    const raw = candidate as CatalogSearchResult;
    return effectiveUnitPrice({ price: raw.price, priceUnit: raw.priceUnit, quantityUnit: raw.unit });
  };

  /**
   * P2-3 (Fix, Codex-Fund): der Batch-Pfad (shared/catalog-batch-suggest.ts::buildBatchCatalogRow)
   * vergleicht bei fehlender candidate-eigener Einheit (quantity_unit) bereits eff.unitLabel (aus
   * price_unit geparst) als Fallback gegen die LV-Einheit — dieser Einzeldialog tat das bisher
   * NICHT und zeigte deshalb bei quantity_unit=null + price_unit="100 ST" + LV-Einheit "m" KEINE
   * Warnung an, obwohl der geteilte Wert fälschlich als €/m gelabelt wurde. Fix: dieselbe geteilte
   * Fallback-Logik anwenden (Symmetrie zum Batch-Pfad).
   */
  const unitsIncompatibleFor = (candidate: CatalogArticleCandidate): boolean => {
    const raw = candidate as CatalogSearchResult;
    const eff = effectiveFor(candidate);
    return unitsIncompatibleMitPriceUnitFallback(eff, raw.unit, position.einheit);
  };

  // FUND 1(c): Position hat bereits eine befüllte 'gesamt'-Kalkulation (bzw. Bestandsdaten
  // ohne mengenBezug-Feld) — ein material-ek-Import würde den EK um den Faktor Positionsmenge
  // zu niedrig ansetzen, siehe shared/kalkulation.ts::materialEkImportBlockiertDurchBestandskalkulation.
  // Betrifft nur den Modus "material-ek"; "direkt-ep" umgeht die Kalkulation komplett.
  const materialEkBlockiertDurchBestand = useMemo(
    () => materialEkImportBlockiertDurchBestandskalkulation(position.kalkulation ?? null),
    [position.kalkulation],
  );

  // P1-E: "direkt-ep" setzt NUR Position.preis, lässt eine vorhandene Kalkulation
  // unangetastet — der nächste Kalk-Speichervorgang leitet den EP aber wieder aus der
  // Kalkulation ab und überschreibt den Direktwert kommentarlos. Siehe
  // shared/kalkulation.ts::direktEpImportBlockiertDurchBestandskalkulation.
  const direktEpBlockiertDurchBestand = useMemo(
    () => direktEpImportBlockiertDurchBestandskalkulation(position.kalkulation ?? null),
    [position.kalkulation],
  );

  const buildMaterialEkKalkulation = (unitPrice: number, quelle: string): PositionsKalkulation => {
    // EP-Bug-Fix: der importierte Katalog-EK ist inhärent PRO EINHEIT (effectiveUnitPrice),
    // nicht für die Gesamtmenge — setzeBlitzZeile setzt bei einer brandneuen Kalkulation aber
    // 'gesamt' (Blitzkalkulations-Semantik). hatKalkulation() MUSS auf dem Stand VOR
    // setzeBlitzZeile geprüft werden, siehe shared/kalkulation.ts::mengenBezugFuerPreisImport.
    //
    // P2-4 (Fix, Codex-Review gpt-5.6): identische Bau-Logik wie der Batch-Pfad
    // (shared/catalog-batch-suggest.ts::buildMaterialEkKalkulationFuerBatchImport) — hier
    // weiterhin lokal, damit `handleApply` unverändert bleibt; der EFFEKTIVE Aufschlag wird
    // unten separat über `ermittleEffektivenBatchAufschlag` (dieselbe Funktion wie der
    // Batch-Toast) aufgelöst, statt weiter blind den globalen Default anzuzeigen.
    const hatteVorherKalkulation = hatKalkulation(position.kalkulation ?? null);
    const basis = setzeBlitzZeile(position.kalkulation ?? null, 'material', { einzelpreis: unitPrice });
    const mitBeschreibung: PositionsKalkulation = {
      ...basis,
      zeilen: basis.zeilen.map((zeile) =>
        zeile.id === BLITZ_ZEILEN_IDS.material
          ? { ...zeile, beschreibung: `Großhandel: ${quelle}` }
          : zeile,
      ),
    };
    return mengenBezugFuerPreisImport(mitBeschreibung, hatteVorherKalkulation);
  };

  const effektiverAufschlagProzent = useMemo(
    () =>
      ermittleEffektivenMaterialAufschlagFuerVorschau(
        position.kalkulation,
        kalkulationsDefaults,
      ),
    [position.kalkulation, kalkulationsDefaults],
  );

  /**
   * FUND 1(c) + 1(d): Grund, warum "Übernehmen" für diesen Kandidaten im aktuellen Modus
   * NICHT sicher ist — null, wenn die Übernahme unbedenklich ist. Wird sowohl zum
   * Deaktivieren des Buttons als auch für den sichtbaren Hinweistext genutzt. Kein
   * stiller Fehlpreis: im Zweifel blockieren statt eine falsche Zahl zu übernehmen.
   */
  const blockReason = (entry: RankedCatalogCandidate): string | null => {
    const eff = effectiveFor(entry.candidate);
    if (eff.value === null) return null; // separat schon über disabled={eff.value === null} abgedeckt
    // P2-F: EK 0/negativ konsistent blockieren — würde bei "material-ek" von
    // setzeBlitzZeile als "leer" verworfen (Blitzzeile bliebe unverändert), ohne dass die
    // UI das sichtbar macht. Gilt für beide Modi (kein stiller Fehlpreis).
    if (nichtUebernehmbarWegenNullOderNegativemPreis(eff.value)) {
      return 'EK 0 € oder negativ — kein gültiger Katalogpreis, nicht automatisch übernehmbar.';
    }
    if (rundetStillAufNull(eff.value)) {
      return 'Preis < 0,01 € je Einheit — für die Kalkulation zu klein.';
    }
    if (mode === 'material-ek' && materialEkBlockiertDurchBestand) {
      return 'Position hat bereits eine Blitzkalkulation — Katalog-Material hier nicht automatisch übernehmbar. Bitte den Materialpreis manuell in der Tiefenkalkulation eintragen.';
    }
    // P1-E: "direkt-ep" überschreibt NUR Position.preis; eine vorhandene Kalkulation bleibt
    // stehen und überschreibt den Direktwert beim nächsten Kalk-Speichervorgang wieder —
    // deshalb ebenfalls blockieren statt einen kaputten Workaround zu erlauben.
    if (mode === 'direkt-ep' && direktEpBlockiertDurchBestand) {
      return 'Position hat bereits eine Kalkulation — ein direkt gesetzter Einheitspreis würde beim nächsten Kalkulations-Speichern wieder überschrieben. Bitte den Preis in der Kalkulation anpassen.';
    }
    return null;
  };

  const handleApply = (entry: RankedCatalogCandidate) => {
    if (
      searchResultContext === null ||
      searchResultContext.positionId !== position.id ||
      searchResultContext.offerDocumentEpoch !== useOfferStore.getState().offerDocumentEpoch
    ) {
      handleOpenChange(false);
      return;
    }
    const raw = entry.candidate as CatalogSearchResult;
    const eff = effectiveFor(entry.candidate);
    if (eff.value === null) return;
    const unitPrice = eff.value;
    if (blockReason(entry)) return; // Defensiv — Button ist in diesem Fall bereits disabled.

    if (mode === 'material-ek') {
      const kalkulation = buildMaterialEkKalkulation(unitPrice, raw.description);
      // P1 (Fix, Codex-Fund): updatePositionKalkulation() kann ablehnen (Netto <= 0 nach dem
      // Update, siehe shared/kalkulation.ts::darfKalkulationPersistiertWerden) — das darf NIE
      // als "übernommen" gemeldet werden. Bei Ablehnung bleibt der Dialog offen (kein
      // handleOpenChange(false) unten), damit der Nutzer den Fehlschlag sieht statt eines
      // stillen No-Ops hinter einer geschlossenen Übernahme-Meldung.
      const write = commitOfferDocumentWriteIfCurrent({
        expectedDocumentEpoch: searchResultContext.offerDocumentEpoch,
        getCurrentDocumentEpoch: () => useOfferStore.getState().offerDocumentEpoch,
        write: () => updatePositionKalkulation(position.id, kalkulation),
      });
      if (!write.committed) {
        handleOpenChange(false);
        return;
      }
      const erfolgreich = write.value;
      if (!erfolgreich) {
        toast({
          title: 'Nicht übernommen',
          description:
            'Diese Kalkulation würde einen nicht-positiven Betrag (0,00 € oder negativ) ergeben — es wurde nichts geändert.',
          variant: 'destructive',
        });
        return;
      }
      const neuerEp = einheitspreisAusKalkulation(kalkulation, menge, kalkulationsDefaults);
      // P2-4 (Fix, Codex-Review gpt-5.6): den TATSÄCHLICH auf `kalkulation` angewendeten
      // Aufschlag melden (effektiverAufschlagProzent), nicht pauschal den globalen Default —
      // siehe Kommentar bei effektiverAufschlagProzent oben.
      toast({
        title: 'Material-EK übernommen',
        description: `${formatEuro(unitPrice)}/${position.einheit || raw.unit || 'Einheit'} EK → neuer EP ${formatEuro(neuerEp)} (+${effektiverAufschlagProzent}% Aufschlag).`,
      });
    } else {
      const write = commitOfferDocumentWriteIfCurrent({
        expectedDocumentEpoch: searchResultContext.offerDocumentEpoch,
        getCurrentDocumentEpoch: () => useOfferStore.getState().offerDocumentEpoch,
        write: () => updatePosition(position.id, { preis: unitPrice }),
      });
      if (!write.committed) {
        handleOpenChange(false);
        return;
      }
      toast({
        title: 'Einheitspreis übernommen',
        description: `${formatEuro(unitPrice)} direkt als EP gesetzt (ohne Aufschlag).`,
      });
    }
    // P2-8 (Fund): dieser Pfad rief bisher setIsOpen(false) DIREKT auf und umging damit den
    // Reset-Zweig in handleOpenChange (searchedOnceRef blieb true, mode/searchError blieben
    // stehen) — das nächste Öffnen übersprang dadurch den Auto-Fetch und zeigte ggf. den alten
    // Modus/veraltete Suchfehler. Fix: über denselben Schließen-Pfad wie Button/ESC/Overlay gehen.
    handleOpenChange(false);
  };

  /**
   * Live-Preis-Übernahme (BEWUSSTE Wahl — der Katalogpreis bleibt Default): läuft durch
   * EXAKT denselben material-ek-Pfad wie Katalogpreise (buildMaterialEkKalkulation →
   * updatePositionKalkulation), nur dass der effektive EK aus dem Live-Quote stammt
   * (shared/live-price.ts::bewerteLiveQuoteEinheit — derselbe Einheiten-Umrechnungspfad
   * + HARTES Einheiten-Veto) und die Zeile die Herkunft einfriert (Snapshot-Pflicht,
   * shared/kalkulation.ts::haengePreisHerkunftAnBlitzMaterial).
   */
  const handleApplyLive = (entry: RankedCatalogCandidate, quote: LiveQuoteMitFrische) => {
    if (
      searchResultContext === null ||
      searchResultContext.positionId !== position.id ||
      searchResultContext.offerDocumentEpoch !== useOfferStore.getState().offerDocumentEpoch
    ) {
      handleOpenChange(false);
      return;
    }
    const raw = entry.candidate as CatalogSearchResult;
    const bewertung = bewerteLiveQuoteEinheit(quote, position.einheit);
    // Defensiv — der Button ist in all diesen Fällen bereits disabled (kein stiller Fehlpreis).
    if (!bewertung.uebernehmbar || bewertung.effektiverPreisProEinheit === null) return;
    const unitPrice = bewertung.effektiverPreisProEinheit;
    if (nichtUebernehmbarWegenNullOderNegativemPreis(unitPrice) || rundetStillAufNull(unitPrice)) return;
    if (materialEkBlockiertDurchBestand) return;

    const herkunft = baueLivePreisHerkunft(quote, raw.supplierId ?? '', bewertung.eff.n);
    const kalkulation = haengePreisHerkunftAnBlitzMaterial(
      buildMaterialEkKalkulation(unitPrice, `${raw.description} (Live-Preis)`),
      herkunft,
    );
    const write = commitOfferDocumentWriteIfCurrent({
      expectedDocumentEpoch: searchResultContext.offerDocumentEpoch,
      getCurrentDocumentEpoch: () => useOfferStore.getState().offerDocumentEpoch,
      write: () => updatePositionKalkulation(position.id, kalkulation),
    });
    if (!write.committed) {
      handleOpenChange(false);
      return;
    }
    if (!write.value) {
      toast({
        title: 'Nicht übernommen',
        description:
          'Diese Kalkulation würde einen nicht-positiven Betrag (0,00 € oder negativ) ergeben — es wurde nichts geändert.',
        variant: 'destructive',
      });
      return;
    }
    const neuerEp = einheitspreisAusKalkulation(kalkulation, menge, kalkulationsDefaults);
    toast({
      title: 'Live-Preis übernommen',
      description: `${formatEuro(unitPrice)}/${position.einheit || raw.unit || 'Einheit'} Live-EK (Quote ${quote.quoteId ?? '—'}, ${formatZeitpunkt(quote.fetchedAt)}) → neuer EP ${formatEuro(neuerEp)} (+${effektiverAufschlagProzent}% Aufschlag).`,
    });
    handleOpenChange(false);
  };

  /**
   * Live-Preis-Platz je Treffer: zeigt ehrlich den Zustand der Händler-API-Integration —
   * deaktiviert mit Tooltip bei fehlender Konfiguration/Discovery, bei 'active' den
   * vorhandenen frischen Quote mit Quelle + Zeitstempel NEBEN dem Katalogpreis. Der
   * Abruf selbst wird hier NIE ausgelöst (v1: Trigger lebt in der Hauptapp) — der
   * Platz sagt das offen.
   */
  const renderLiveZeile = (entry: RankedCatalogCandidate) => {
    const raw = entry.candidate as CatalogSearchResult;
    const supplierId = raw.supplierId;
    if (!supplierId) return null;
    const liveInfo = liveStatusBySupplier[supplierId];
    if (!liveInfo) return null;

    const zeile = (text: string, tooltip: string, klasse = 'text-gray-400') => (
      <p className={`mt-0.5 flex items-center gap-1 text-xs ${klasse}`} title={tooltip}>
        <Zap className="h-3 w-3 flex-shrink-0" />
        {text}
      </p>
    );

    if (!liveInfo.verfuegbar) {
      return zeile(
        'Live-Preis: keine Händler-API konfiguriert',
        liveInfo.hinweis ??
          'Keine Händler-API konfiguriert — die Live-Preis-Tabellen existieren in dieser Umgebung (noch) nicht.',
      );
    }
    if (liveInfo.status === 'none') {
      return zeile(
        'Live-Preis: keine Händler-API konfiguriert',
        'Für diesen Lieferanten ist keine Händler-API hinterlegt.',
      );
    }
    if (liveInfo.status === 'discovery') {
      return zeile(
        'Live-Preis: API im Discovery-Modus — noch keine Preise',
        'Die Händler-API wird gerade eingerichtet (Discovery) — es liegen noch keine Live-Preise vor.',
      );
    }
    if (liveInfo.status === 'error') {
      return zeile(
        'Live-Preis: Händler-API meldet einen Fehler',
        'Die Händler-API-Integration ist im Fehlerzustand — bitte in der Hauptapp prüfen.',
        'text-red-600',
      );
    }

    const quote = liveQuoteByKey[`${supplierId}::${raw.articleNumber}`];
    if (!quote) {
      return zeile(
        'Live-Preis: noch kein Abruf vorhanden',
        'Der Live-Abruf wird in der Hauptapp ausgelöst — hier wird nur angezeigt, was bereits geholt wurde.',
      );
    }

    if (quote.frische === 'live-frisch') {
      const bewertung = bewerteLiveQuoteEinheit(quote, position.einheit);
      if (bewertung.einheitStatus === 'unsupported-unit') {
        return zeile(
          'Live-Preis: Einheit nicht unterstützt (unsupported-unit) — nicht übernehmbar',
          bewertung.grund ?? 'Einheit des Live-Preises unbekannt oder unvereinbar.',
          'text-amber-700',
        );
      }
      const liveBlockiert =
        !bewertung.uebernehmbar ||
        bewertung.effektiverPreisProEinheit === null ||
        rundetStillAufNull(bewertung.effektiverPreisProEinheit) ||
        materialEkBlockiertDurchBestand;
      const liveBlockGrund = materialEkBlockiertDurchBestand
        ? 'Position hat bereits eine Blitzkalkulation — Live-Preis hier nicht automatisch übernehmbar.'
        : bewertung.grund;
      return (
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
          <span className="flex items-center gap-1 text-sky-700" title="Live-Preis der Händler-API — der Katalogpreis bleibt Standard; Übernahme ist eine bewusste Wahl.">
            <Zap className="h-3 w-3 flex-shrink-0" />
            Live: {bewertung.effektiverPreisProEinheit !== null ? formatEuro(bewertung.effektiverPreisProEinheit) : '—'}
            /{nachDivisorEinheitLabel(bewertung.eff, quote.orderUnit || position.einheit)}
            {' · '}
            {quote.quelle ?? 'Händler-API'}
            {' · '}
            {formatZeitpunkt(quote.fetchedAt)}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
            disabled={liveBlockiert}
            title={liveBlockiert ? (liveBlockGrund ?? undefined) : 'Live-Preis als Material-EK übernehmen (Herkunft wird an der Zeile eingefroren).'}
            onClick={() => handleApplyLive(entry, quote)}
          >
            Live-EK übernehmen
          </Button>
        </div>
      );
    }
    if (quote.frische === 'stale') {
      return zeile(
        `Live-Preis veraltet (stale, Stand ${formatZeitpunkt(quote.fetchedAt)}) — nicht übernehmbar`,
        'Die TTL des letzten Live-Abrufs ist abgelaufen — ein neuer Abruf erfolgt in der Hauptapp.',
        'text-amber-700',
      );
    }
    return zeile(
      `Live-Preis: Status „${quote.frische}"`,
      quote.errorCode ? `Fehlercode: ${quote.errorCode}` : 'Der letzte Live-Abruf war nicht erfolgreich.',
      'text-amber-700',
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-gray-500 hover:text-emerald-700"
          title="Katalog-Vorschlag (Großhandel)"
        >
          <PackageSearch className="h-4 w-4" />
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <PackageSearch className="h-5 w-5 text-emerald-600" />
            <span>Katalog-Vorschlag: Großhandelsartikel</span>
          </DialogTitle>
          <DialogDescription>
            Passende Katalogartikel für „{position.name || position.beschreibung || 'diese Position'}" suchen und
            als Material-EK oder direkten Einheitspreis übernehmen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-1">
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void runSearch(query);
                }
              }}
              placeholder="Suchbegriff (Bezeichnung oder Artikelnummer)"
              className="flex-1"
            />
            <Button type="button" variant="outline" onClick={() => void runSearch(query)} disabled={isSearching}>
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          {searchError && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              {searchError}
            </div>
          )}

          {/* P2-3 (Fix, Codex-Fund): der Batch-Pfad nutzt `truncated` bereits (höheres Limit +
              Sperre der automatischen Vorauswahl); dieser Einzeldialog wählt ohnehin nichts
              automatisch aus, daher reicht hier ein sichtbarer Hinweis statt einer Sperre. */}
          {!isSearching && truncated && (
            <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Ergebnisse gekürzt — Suche präzisieren, damit auch weniger auffällige Treffer sichtbar werden.</span>
            </div>
          )}

          {!isSearching && ranked.length > 0 && (
            <>
              {/* Modus-Auswahl — identisch zu X84PriceImport */}
              <RadioGroup value={mode} onValueChange={(value) => setMode(value as ApplyMode)} className="space-y-1">
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="material-ek" id="catalog-mode-material-ek" className="mt-0.5" />
                  <Label htmlFor="catalog-mode-material-ek" className="font-normal cursor-pointer">
                    {/* P2-4 (Fix, Codex-Review gpt-5.6): effektiverAufschlagProzent statt pauschal
                        des globalen Defaults — siehe Kommentar bei dessen Definition oben. */}
                    Als Material-EK in Kalkulation (+{effektiverAufschlagProzent}% Aufschlag)
                  </Label>
                </div>
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="direkt-ep" id="catalog-mode-direkt-ep" className="mt-0.5" />
                  <Label htmlFor="catalog-mode-direkt-ep" className="font-normal cursor-pointer">
                    Direkt als Einheitspreis übernehmen (ohne Aufschlag)
                  </Label>
                </div>
              </RadioGroup>

              {mode === 'material-ek' && materialEkBlockiertDurchBestand && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Position hat bereits eine Blitzkalkulation — Katalog-Material hier nicht automatisch
                    übernehmbar. Bitte den Materialpreis manuell in der Tiefenkalkulation eintragen.
                  </span>
                </div>
              )}

              {mode === 'direkt-ep' && direktEpBlockiertDurchBestand && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Position hat bereits eine Kalkulation — ein direkt gesetzter Einheitspreis würde beim nächsten
                    Kalkulations-Speichern wieder überschrieben. Bitte den Preis in der Kalkulation anpassen.
                  </span>
                </div>
              )}

              <div className="border rounded-md divide-y">
                {ranked.map((entry) => {
                  const raw = entry.candidate as CatalogSearchResult;
                  const eff = effectiveFor(entry.candidate);
                  const rawUnitLabel = raw.priceUnit || raw.unit || 'Einheit';
                  const reason = blockReason(entry);
                  // Preisfrische-Badge (shared/preis-frische.ts): frisch/älter/abgelaufen aus
                  // price_valid_from/until + updated_at — 'unbekannt' zeigt bewusst KEIN Badge
                  // (kein Raten), ältere Server-Antworten ohne die Felder bleiben badge-los.
                  const frische = bewertePreisFrische(raw, new Date());
                  return (
                    <div key={raw.id} className="p-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={ZONE_BADGE_CLASS[entry.zone]}>{ZONE_LABEL[entry.zone]}</Badge>
                          {raw.articleNumber && (
                            <span className="text-xs text-gray-500">Art.-Nr. {raw.articleNumber}</span>
                          )}
                          {frische.stufe !== 'unbekannt' && (
                            <span
                              className={`rounded border px-1.5 py-0.5 text-[11px] ${FRISCHE_BADGE_CLASS[frische.stufe]}`}
                              title={`Preisstand aus gültig-ab/aktualisiert-am${raw.priceValidUntil ? ` · gültig bis ${formatZeitpunkt(raw.priceValidUntil)}` : ''}`}
                            >
                              {frische.label}
                            </span>
                          )}
                          {unitsIncompatibleFor(entry.candidate) && (
                            <span className="flex items-center gap-1 text-xs text-amber-700">
                              <AlertTriangle className="h-3 w-3" />
                              Einheiten evtl. inkompatibel
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium truncate" title={raw.description}>
                          {raw.description}
                        </p>
                        {raw.price !== null && (
                          <p className="text-xs text-gray-500">
                            {formatEuro(raw.price)}/{rawUnitLabel}
                            {eff.value !== null && eff.n > 1 && (
                              <>
                                {' → '}
                                <span className="font-medium text-gray-700">
                                  {/* P2-5 (Fix, Codex-Review gpt-5.6): eff.unitLabel (aus priceUnit
                                      geparst, bereits vom Divisor-Präfix bereinigt) hat Vorrang vor
                                      raw.unit (quantityUnit) — bei Fixed-Width-DATANORM kann
                                      quantityUnit selbst den rohen Preiseinheit-Text tragen (z.B.
                                      "100 ST"), was sonst einen bereits durch 100 geteilten Wert
                                      fälschlich wieder als "je 100 ST" anzeigen würde. */}
                                  {formatEuro(eff.value)}/{nachDivisorEinheitLabel(eff, raw.unit || position.einheit)}
                                </span>
                              </>
                            )}
                          </p>
                        )}
                        {renderLiveZeile(entry)}
                        {reason && (
                          <p className="flex items-center gap-1 text-xs text-amber-700 mt-1">
                            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                            {reason}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
                        disabled={eff.value === null || reason !== null}
                        title={reason ?? undefined}
                        onClick={() => handleApply(entry)}
                      >
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        Übernehmen
                      </Button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!isSearching && ranked.length === 0 && !searchError && (
            <p className="text-sm text-gray-500 text-center py-4">
              {query.trim() ? 'Keine passenden Katalogartikel gefunden.' : 'Suchbegriff eingeben.'}
            </p>
          )}

          {isSearching && (
            <div className="flex items-center justify-center text-sm text-gray-500 py-4">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Suche läuft...
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CatalogSuggestDialog;
